import { SEED_COSTS, calculateDubbingSeedCost } from "@/lib/constants/seeds";

export type ActionPricingId =
  | "new_project"
  | "reoptimize"
  | "video_ideas"
  | "channel_description"
  | "precision_trim_export"
  | "midroll_analysis"
  | "diagnose"
  | "diagnose_long_video"
  | "diagnose_synthetic_ad"
  | "diagnose_music"
  | "thumbnail_generation"
  | "thumbnail_edit"
  | "autoclips_analysis"
  | "autoclips_reframe"
  | "translation_language"
  | "intelligence_analysis"
  | "why_analysis"
  | "precision_trim_analysis"
  | "dubbing";

export type ActionChargeTiming = "upfront" | "on_success" | "free_after_quota";

export type ActionPricingModel = "fixed" | "per_item" | "per_duration";

export interface ResolveActionChargeOptions {
  itemCount?: number;
  durationSeconds?: number;
  dubbingTier?: "automatic";
}

export interface ResolvedActionCharge {
  id: ActionPricingId;
  amount: number;
  pricingModel: ActionPricingModel;
  chargeTiming: ActionChargeTiming;
  unitAmount?: number;
  unitCount?: number;
  unitLabel?: string;
}

export interface ActionPricingConfig {
  id: ActionPricingId;
  pricingModel: ActionPricingModel;
  chargeTiming: ActionChargeTiming;
  labelKey?: string;
  catalog: boolean;
  resolve: (options?: ResolveActionChargeOptions) => ResolvedActionCharge;
}

export interface SeedCostCatalogRow {
  id: ActionPricingId;
  labelKey: string;
  amountDisplay: string;
}

function normalizeItemCount(value: number | undefined): number {
  if (!Number.isFinite(value) || (value ?? 0) <= 0) {
    return 1;
  }
  return Math.max(1, Math.floor(value ?? 1));
}

const ACTION_PRICING: Record<ActionPricingId, ActionPricingConfig> = {
  new_project: {
    id: "new_project",
    pricingModel: "fixed",
    chargeTiming: "upfront",
    labelKey: "new_video_upload",
    catalog: true,
    resolve: () => ({
      id: "new_project",
      amount: SEED_COSTS.new_video_upload,
      pricingModel: "fixed",
      chargeTiming: "upfront",
    }),
  },
  reoptimize: {
    id: "reoptimize",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "reoptimize",
    catalog: true,
    resolve: () => ({
      id: "reoptimize",
      amount: SEED_COSTS.reoptimize,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  video_ideas: {
    id: "video_ideas",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "video_ideas",
    catalog: true,
    resolve: () => ({
      id: "video_ideas",
      amount: SEED_COSTS.video_ideas,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  channel_description: {
    id: "channel_description",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "channel_description",
    catalog: true,
    resolve: () => ({
      id: "channel_description",
      amount: SEED_COSTS.channel_description,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  precision_trim_export: {
    id: "precision_trim_export",
    pricingModel: "fixed",
    chargeTiming: "upfront",
    labelKey: "precision_trim_export",
    catalog: true,
    resolve: () => ({
      id: "precision_trim_export",
      amount: SEED_COSTS.precision_trim_export,
      pricingModel: "fixed",
      chargeTiming: "upfront",
    }),
  },
  midroll_analysis: {
    id: "midroll_analysis",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "midroll_analysis",
    catalog: true,
    resolve: () => ({
      id: "midroll_analysis",
      amount: SEED_COSTS.midroll_analysis,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  diagnose: {
    id: "diagnose",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "diagnose",
    catalog: true,
    resolve: () => ({
      id: "diagnose",
      amount: SEED_COSTS.diagnose,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  diagnose_long_video: {
    id: "diagnose_long_video",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "diagnose",
    catalog: true,
    resolve: () => ({
      id: "diagnose_long_video",
      amount: SEED_COSTS.diagnose_long_video,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  diagnose_synthetic_ad: {
    id: "diagnose_synthetic_ad",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "diagnose",
    catalog: true,
    resolve: () => ({
      id: "diagnose_synthetic_ad",
      amount: SEED_COSTS.diagnose_synthetic_ad,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  diagnose_music: {
    id: "diagnose_music",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "diagnose",
    catalog: true,
    resolve: () => ({
      id: "diagnose_music",
      amount: SEED_COSTS.diagnose_music ?? SEED_COSTS.diagnose_synthetic_ad,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  thumbnail_generation: {
    id: "thumbnail_generation",
    pricingModel: "per_item",
    chargeTiming: "on_success",
    labelKey: "thumbnail",
    catalog: true,
    resolve: (options) => {
      const itemCount = normalizeItemCount(options?.itemCount);
      return {
        id: "thumbnail_generation",
        amount: itemCount * SEED_COSTS.thumbnail,
        pricingModel: "per_item",
        chargeTiming: "on_success",
        unitAmount: SEED_COSTS.thumbnail,
        unitCount: itemCount,
        unitLabel: "image",
      };
    },
  },
  thumbnail_edit: {
    id: "thumbnail_edit",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    catalog: false,
    resolve: () => ({
      id: "thumbnail_edit",
      amount: SEED_COSTS.thumbnail,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  autoclips_analysis: {
    id: "autoclips_analysis",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "autoclips",
    catalog: true,
    resolve: () => ({
      id: "autoclips_analysis",
      amount: SEED_COSTS.autoclips,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  autoclips_reframe: {
    id: "autoclips_reframe",
    pricingModel: "fixed",
    chargeTiming: "on_success",
    labelKey: "autoclips_reframe",
    catalog: true,
    resolve: () => ({
      id: "autoclips_reframe",
      amount: SEED_COSTS.autoclips_reframe,
      pricingModel: "fixed",
      chargeTiming: "on_success",
    }),
  },
  translation_language: {
    id: "translation_language",
    pricingModel: "per_item",
    chargeTiming: "on_success",
    labelKey: "translation",
    catalog: true,
    resolve: (options) => {
      const itemCount = normalizeItemCount(options?.itemCount);
      return {
        id: "translation_language",
        amount: itemCount * SEED_COSTS.translation,
        pricingModel: "per_item",
        chargeTiming: "on_success",
        unitAmount: SEED_COSTS.translation,
        unitCount: itemCount,
        unitLabel: "language",
      };
    },
  },
  intelligence_analysis: {
    id: "intelligence_analysis",
    pricingModel: "fixed",
    chargeTiming: "upfront",
    labelKey: "intelligence_analysis",
    catalog: true,
    resolve: () => ({
      id: "intelligence_analysis",
      amount: SEED_COSTS.intelligence_analysis,
      pricingModel: "fixed",
      chargeTiming: "upfront",
    }),
  },
  why_analysis: {
    id: "why_analysis",
    pricingModel: "fixed",
    chargeTiming: "upfront",
    labelKey: "why_analysis",
    catalog: true,
    resolve: () => ({
      id: "why_analysis",
      amount: SEED_COSTS.why_analysis,
      pricingModel: "fixed",
      chargeTiming: "upfront",
    }),
  },
  precision_trim_analysis: {
    id: "precision_trim_analysis",
    pricingModel: "fixed",
    chargeTiming: "upfront",
    labelKey: "precision_trim_analysis",
    catalog: true,
    resolve: () => ({
      id: "precision_trim_analysis",
      amount: SEED_COSTS.precision_trim_analysis,
      pricingModel: "fixed",
      chargeTiming: "upfront",
    }),
  },
  dubbing: {
    id: "dubbing",
    pricingModel: "per_duration",
    chargeTiming: "on_success",
    labelKey: "dubbing",
    catalog: true,
    resolve: (options) => {
      const durationSeconds = options?.durationSeconds ?? 0;
      const amount = calculateDubbingSeedCost(durationSeconds);
      const unitCount = amount / SEED_COSTS.dubbing_per_30_seconds;
      return {
        id: "dubbing",
        amount,
        pricingModel: "per_duration",
        chargeTiming: "on_success",
        unitAmount: SEED_COSTS.dubbing_per_30_seconds,
        unitCount,
        unitLabel: "30_seconds",
      };
    },
  },
};

const SEED_COST_CATALOG_ORDER: ActionPricingId[] = [
  "new_project",
  "autoclips_analysis",
  "autoclips_reframe",
  "precision_trim_analysis",
  "precision_trim_export",
  "dubbing",
  "diagnose",
  "diagnose_long_video",
  "diagnose_synthetic_ad",
  "diagnose_music",
  "reoptimize",
  "video_ideas",
  "channel_description",
  "translation_language",
  "midroll_analysis",
  "intelligence_analysis",
  "why_analysis",
  "thumbnail_generation",
];

export function getActionPricingConfig(
  id: ActionPricingId
): ActionPricingConfig {
  return ACTION_PRICING[id];
}

export function resolveActionCharge(
  id: ActionPricingId,
  options?: ResolveActionChargeOptions
): ResolvedActionCharge {
  return getActionPricingConfig(id).resolve(options);
}

export function getActionPriceSeedAmount(
  id: ActionPricingId,
  options?: ResolveActionChargeOptions
): number {
  return resolveActionCharge(id, options).amount;
}

export function getSeedCostCatalogEntries(): ActionPricingConfig[] {
  return SEED_COST_CATALOG_ORDER.map((id) => ACTION_PRICING[id]);
}

export function getSeedCostCatalogRows(): SeedCostCatalogRow[] {
  return getSeedCostCatalogEntries().map((entry) => ({
    id: entry.id,
    labelKey: entry.labelKey ?? entry.id,
    amountDisplay:
      entry.id === "dubbing"
        ? String(SEED_COSTS.dubbing_per_30_seconds)
        : String(resolveActionCharge(entry.id).amount),
  }));
}
